import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Badge,
  BUTTON_CLASS,
  DataTable,
  Eyebrow,
  FileKindIcon,
  formatUtcDate,
  formatUtcDateTime,
  MetaRow,
  neutralButtonStyle,
  StatusDot,
  token,
  type DataTableColumn,
} from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { CARD_BODY_CLASS, CARD_SURFACE_CLASS, CardBody, CardGrid } from '@/components/CardGrid';
import { CheckIcon, MoreActionsIcon } from '@/components/FolderView/FolderIcons';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { isViewable } from '@/components/Viewer/viewable';
import { formatBytes } from '@/lib/formatBytes';
import { daysUntil, isExpiringSoon } from '@/lib/expiry';
import type { ViewMode } from '@/lib/useViewMode';
import {
  requestRehydration,
  retentionClassSchema,
  type FileSummary,
  type RetentionClass,
} from '@/lib/api';
import { useFileDownload } from '@/components/FolderView/useFileDownload';

/**
 * One file, as a row — the table, the card, and every cell and act they share
 * (SPEC §6, FIL-4..FIL-8, FIL-10, RET-4..RET-9).
 *
 * `FolderView.tsx` is the page: the URL, the folder's own acts, the viewers,
 * and the upload panel. This module is what the page hands its files to.
 * `FileTable` picks the layout from `mode` and both layouts render the same
 * sub-components — `FileNameLink`, `VerificationDot`, `TierFlag`,
 * `RowActions` — so a fact about a file is drawn by one component whichever
 * shape the viewport gives it.
 *
 * `useFileDownload.ts` beside this is the one way a download starts, shared with
 * the two viewers the page mounts.
 */

export function FileTable({
  files,
  mode,
  ownScope,
  agencyFilter,
  showRetention,
  expiryWindowDays,
  onView,
  onDownloadFailed,
  onDownloadStarted,
  onRehydrated,
  confirming,
  busyId,
  onAsk,
  onCancel,
  onDelete,
  onRetain,
}: {
  files: FileSummary[];
  /** The card/table layout, derived by `FolderView` above from viewport width. */
  mode: ViewMode;
  /**
   * Whether this reader's agency is scoped to its own uploads here (CON-24).
   *
   * It changes **one string** and nothing else: the rows, the columns and every
   * act on them are identical, because scope narrows the *subject* of a read
   * rather than any cell of §5.3's matrix. What it buys is that an empty table
   * stops being a claim about the folder this reader cannot check.
   */
  ownScope: boolean;
  /**
   * The agency the header's filter has narrowed to, or `undefined` for "All
   * agencies" — reaches only the empty-state string. `ownScope` and this are
   * never both set: the filter trigger only renders when `!ownScope`.
   */
  agencyFilter: string | undefined;
  /** RET-5's Evidence gate — hides the column, not only the ability to edit it. */
  showRetention: boolean;
  /**
   * The room's active window in days (RET-12), for the "expiring soon" hint;
   * absent means the 180-day default. One value for the whole table — the window
   * is the room's, not the file's.
   */
  expiryWindowDays?: number | undefined;
  /**
   * FIL-8a: open this row in the image viewer instead of downloading it.
   * Passed on to every row; `FileNameLink` decides per file whether it
   * applies, because only the filename says whether a browser can paint it.
   */
  onView: (fileId: string) => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
  onRehydrated: (message: string) => void;
  /** The id of the row confirming a delete, if any. */
  confirming: string | undefined;
  busyId: string | undefined;
  onAsk: (id: string) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onRetain: (id: string, retentionClass: RetentionClass) => void;
}) {
  if (mode === 'cards') {
    return (
      <FileCardList
        files={files}
        ownScope={ownScope}
        agencyFilter={agencyFilter}
        showRetention={showRetention}
        expiryWindowDays={expiryWindowDays}
        onView={onView}
        onDownloadFailed={onDownloadFailed}
        onDownloadStarted={onDownloadStarted}
        onRehydrated={onRehydrated}
        confirming={confirming}
        busyId={busyId}
        onAsk={onAsk}
        onCancel={onCancel}
        onDelete={onDelete}
        onRetain={onRetain}
      />
    );
  }

  /*
    Sorting is on, and the comment here said it was off "because the server
    orders newest-first out of the row key (FIL-4), and it pages — so a
    client-side sort would order one page". **The rule is right and the second
    clause stopped being true**: `useFiles` follows `continuationToken` to
    exhaustion before it settles `loaded` (`src/lib/useFiles.ts`), which is the
    same fact the summary line above leans on to state a byte total. The client
    holds the whole folder, so ordering it is honest.

    The server's newest-first is no longer what the reader sees, deliberately —
    `FolderView` sorts by name above, and this is the header state that says so.
    `AuditSurface` is the surface that genuinely pages, and its sorting stays off.
  */
  const columns: DataTableColumn<FileSummary>[] = [
    {
      id: 'name',
      header: FOLDER_COPY.columns.name,
      sortable: true,
      sortValue: (file) => file.name,
      // The name is the row's anchor, so anything that is true of the *file*
      // rather than of one of its attributes belongs here — which is why
      // `kind` prefixes it as an icon rather than keeping its own column.
      // Nothing branches on `kind` (FIL-4); the icon is informational, same as
      // the badge it replaced. A mismatch used to flag here too, redundantly
      // with the Verification column's own red dot — the dot is the one place
      // that fact lives now.
      cell: (file) => (
        <div className="flex items-start gap-2">
          <FileKindIcon
            kind={file.kind}
            label={FOLDER_COPY.kinds[file.kind]}
            className="mt-0.5 shrink-0"
          />

          <div className="flex flex-col gap-1">
            <FileNameLink
              file={file}
              disabled={
                confirming === file.id ||
                file.tierState === 'Archive' ||
                file.tierState === 'Rehydrating'
              }
              onView={onView}
              onDownloadFailed={onDownloadFailed}
              onDownloadStarted={onDownloadStarted}
            />

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <TierFlag file={file} />
              <ExpiringFlag file={file} windowDays={expiryWindowDays} />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'size',
      header: FOLDER_COPY.columns.size,
      align: 'end',
      sortable: true,
      // The raw byte count, never the formatted string: "9.9 MB" sorts after
      // "10.4 GB" on any comparison of the text.
      sortValue: (file) => file.sizeBytes,
      cell: (file) => (
        <span style={{ color: token('bp-text-secondary'), fontVariantNumeric: 'tabular-nums' }}>
          {formatBytes(file.sizeBytes, FOLDER_COPY.units)}
        </span>
      ),
    },
    {
      id: 'verification',
      header: FOLDER_COPY.columns.verification,
      sortable: true,
      /*
        `hashStatus`, which is what the dot renders — not `sha256`, whose
        absence is a *pending* hash rather than no hash at all (FIL-6).

        Ascending puts `mismatch` before `ok` before `pending`, alphabetically
        and usefully: somebody sorting this column is looking for the file whose
        hash did not match, and one click puts it at the top. That it falls out
        of the alphabet is luck, so it is written down here rather than left to
        look deliberate to the next reader and accidental to the one after.
      */
      sortValue: (file) => file.hashStatus,
      cell: (file) => <VerificationDot file={file} />,
    },
    {
      id: 'addedBy',
      header: FOLDER_COPY.columns.addedBy,
      sortable: true,
      // The person, not the agency: the cell stacks both, and the name is the
      // line a reader scans. Sorting by agency is a different question, and
      // nobody has asked it.
      sortValue: (file) => file.uploadedBy.name,
      // Two lines rather than one long `person · agency` string (FIL-5). Both
      // facts stay and both sides still see them; they simply stack, because a
      // rank, a surname, and a parish agency's full name run past the width any
      // column here can give them.
      cell: (file) => (
        <div className="flex flex-col">
          <span style={{ color: token('bp-text') }}>{file.uploadedBy.name}</span>
          <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
            {file.uploadedBy.agencyName}
          </span>
        </div>
      ),
    },
    {
      id: 'addedAt',
      header: FOLDER_COPY.columns.addedAt,
      sortable: true,
      // The ISO string off the wire, not the formatted date: `Feb 11, 2026`
      // sorts alphabetically, which is by month name.
      sortValue: (file) => file.uploadedAt,
      // MetaRow owns the amber-for-time rule, so the treatment is right here
      // without this component remembering it. The table shows the time of day
      // as well; the mobile card below keeps to the date (`formatDate`), which
      // is all its width allows.
      cell: (file) => (
        <MetaRow
          time={formatUtcDateTime(file.uploadedAt, FOLDER_COPY.dateLocale)}
          dateTime={file.uploadedAt}
        />
      ),
    },
    /**
     * RET-4..RET-8, its own column now — not a badge under the file name.
     * `showRetention` (RET-5's Evidence gate) hides the whole column, not just
     * the ability to change what is in it: retention is set by the *uploading*
     * agency (PERM-6), so a Docs-plan viewer could otherwise still read a
     * counterpart's Evidence-plan class on a file that is not theirs.
     */
    {
      id: 'actions',
      header: FOLDER_COPY.columns.actions,
      align: 'end',
      /**
       * Delete, offered only where the API said this caller may (§5.3,
       * PERM-4) — retention has its own column and its own flag
       * (`canSetRetention`, read there instead). Request retrieval, offered
       * on the file's archived state rather than the caller's — RET-9/PERM-6
       * again, `RowActions`' own doc comment has the full reasoning.
       * Visibility, not authorization either way — the API refuses regardless
       * (SEC-5).
       */
      cell: (file) =>
        hasRowActions(file, showRetention) ? (
          <RowActions
            file={file}
            confirming={confirming === file.id}
            busy={busyId === file.id}
            showRetention={showRetention}
            onAsk={onAsk}
            onCancel={onCancel}
            onDelete={onDelete}
            onRetain={onRetain}
            onDownloadFailed={onDownloadFailed}
            onDownloadStarted={onDownloadStarted}
            onRehydrateFailed={onDownloadFailed}
            onRehydrateStarted={onDownloadStarted}
            onRehydrated={onRehydrated}
          />
        ) : null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={files}
      rowKey={(file) => file.id}
      caption={FOLDER_COPY.states.tableCaption}
      emptyState={
        agencyFilter !== undefined
          ? FOLDER_COPY.states.emptyFiltered(agencyFilter)
          : ownScope
            ? FOLDER_COPY.states.emptyOwnScope
            : FOLDER_COPY.states.empty
      }
      // `rows` already arrives in this order — `FolderView` sorts once for every
      // reader of the list. This is what marks the header and makes the first
      // click on File a flip rather than a no-op.
      defaultSort={{ id: 'name', direction: 'asc' }}
    />
  );
}

/**
 * Below 1024px: one `FileCard` per file, stacked, instead of `DataTable`'s
 * rows. `flex flex-col`, not `CardGrid`'s multi-column nav grid — a file
 * card carries far more per-item data than a folder or agency tile, and a
 * two-column layout would crowd it.
 *
 * The empty state matches `DataTable`'s own (`emptyState={FOLDER_COPY.states.empty}`)
 * rather than diverging by viewport — an empty folder is the same fact
 * either way.
 *
 * The single-column rationale above is superseded: file cards now use the
 * shared grid so Files has consistent card dimensions at every level.
 */
function FileCardList({
  files,
  ownScope,
  agencyFilter,
  showRetention,
  expiryWindowDays,
  onView,
  onDownloadFailed,
  onDownloadStarted,
  onRehydrated,
  confirming,
  busyId,
  onAsk,
  onCancel,
  onDelete,
  onRetain,
}: {
  files: FileSummary[];
  /** CON-24, and it reaches the same string the table's `emptyState` does. */
  ownScope: boolean;
  /** The table's own — see `FileTable`'s doc comment on the same prop. */
  agencyFilter: string | undefined;
  showRetention: boolean;
  /** The room's window (RET-12) for the expiring-soon hint; see `FileTable`. */
  expiryWindowDays?: number | undefined;
  /**
   * FIL-8a: open this row in the image viewer instead of downloading it.
   * Passed on to every row; `FileNameLink` decides per file whether it
   * applies, because only the filename says whether a browser can paint it.
   */
  onView: (fileId: string) => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
  onRehydrated: (message: string) => void;
  confirming: string | undefined;
  busyId: string | undefined;
  onAsk: (id: string) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onRetain: (id: string, retentionClass: RetentionClass) => void;
}) {
  if (files.length === 0) {
    return (
      <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {agencyFilter !== undefined
          ? FOLDER_COPY.states.emptyFiltered(agencyFilter)
          : ownScope
            ? FOLDER_COPY.states.emptyOwnScope
            : FOLDER_COPY.states.empty}
      </p>
    );
  }

  return (
    <CardGrid label={FOLDER_COPY.states.tableCaption}>
      {files.map((file) => (
        <li key={file.id}>
          <FileCard
            file={file}
            showRetention={showRetention}
            expiryWindowDays={expiryWindowDays}
            confirming={confirming === file.id}
            busy={busyId === file.id}
            onAsk={onAsk}
            onCancel={onCancel}
            onDelete={onDelete}
            onRetain={onRetain}
            onView={onView}
            onDownloadFailed={onDownloadFailed}
            onDownloadStarted={onDownloadStarted}
            onRehydrated={onRehydrated}
          />
        </li>
      ))}
    </CardGrid>
  );
}

/**
 * One file, reformatted for a phone or tablet — the same information and the
 * same controls as a table row, none of it re-derived: `KindIcon`, `TierFlag`,
 * `VerificationDot`, `RowActions`, and `FileNameLink` are the
 * exact sub-components the desktop columns call, just laid out in a stack
 * instead of a row. `FOLDER_COPY.columns.*` doubles as each fact's caption
 * here — a table row leans on its header for that; a card has no header row
 * to borrow, so the label moves onto the card itself.
 *
 * `RowActions` sits beside the name rather than at the card's trailing edge,
 * on `DataTable`'s own `actions` column's position — its confirm/cancel pair
 * renders in place exactly as it does in a row, so the card needs no delete
 * logic of its own.
 *
 * **It is `CardBody` now, the same shape an agency and a folder tile are.** It
 * used to be hand-laid on `CARD_SURFACE_CLASS` alone, and drifted: a `text-lg`
 * title over a `text-xs` body with a `text-sm` size line, against `text-sm`
 * over `text-xs` one level up. Sharing a surface class was not enough to keep
 * three tiles the same tile; sharing the inside is.
 *
 * The label-per-fact note above still holds for the *date* — `FOLDER_COPY.card`
 * carries "Added" because a card has no column header to borrow. It is
 * deliberately **not** "Last activity": that is a file's upload, and an upload
 * date is a different fact from the folder's own activity one level up.
 */
function FileCard({
  file,
  showRetention,
  expiryWindowDays,
  confirming,
  busy,
  onAsk,
  onCancel,
  onDelete,
  onRetain,
  onView,
  onDownloadFailed,
  onDownloadStarted,
  onRehydrated,
}: {
  file: FileSummary;
  showRetention: boolean;
  /** The room's window (RET-12) for the expiring-soon hint; see `FileTable`. */
  expiryWindowDays?: number | undefined;
  confirming: boolean;
  busy: boolean;
  onAsk: (id: string) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onRetain: (id: string, retentionClass: RetentionClass) => void;
  /**
   * FIL-8a: open this row in the image viewer instead of downloading it.
   * Passed on to every row; `FileNameLink` decides per file whether it
   * applies, because only the filename says whether a browser can paint it.
   */
  onView: (fileId: string) => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
  onRehydrated: (message: string) => void;
}) {
  return (
    <article className={CARD_SURFACE_CLASS}>
      <CardBody
        icon={<FileKindIcon kind={file.kind} label={FOLDER_COPY.kinds[file.kind]} />}
        /* The dot moves under the kind glyph, out of a trailing row of its own.
           Both are one-glance facts *about the file itself* rather than lines of
           its description, so they belong in the same badge column — and the
           bottom row they used to share with the size existed only to hold them
           apart. */
        iconFooter={<VerificationDot file={file} />}
        title={
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <FileNameLink
              file={file}
              disabled={
                confirming || file.tierState === 'Archive' || file.tierState === 'Rehydrating'
              }
              onView={onView}
              onDownloadFailed={onDownloadFailed}
              onDownloadStarted={onDownloadStarted}
            />
            <TierFlag file={file} />
            <ExpiringFlag file={file} windowDays={expiryWindowDays} />
          </div>
        }
        /*
          Two rows, not four. The uploader and their agency share one line —
          `bp-text` on the name, the wrapper's own `bp-text-secondary` on the
          agency — and the size and the added date share the next, on
          `MetaRow`'s own amber-for-time rule rather than a hand-rolled one. A
          tile that could carry the whole roster of facts this file has still
          has to be scannable at thirty in a grid, and four stacked lines per
          tile was the thing standing in the way of that.
        */
        detail={
          <span className="flex flex-wrap items-baseline gap-x-1">
            <span style={{ color: token('bp-text') }}>{file.uploadedBy.name}</span>
            <span aria-hidden="true">/</span>
            <span>{file.uploadedBy.agencyName}</span>
          </span>
        }
        meta={
          <div
            className={`flex flex-wrap items-center gap-1 ${CARD_BODY_CLASS}`}
            style={{ color: token('bp-text-secondary') }}
          >
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatBytes(file.sizeBytes, FOLDER_COPY.units)}
            </span>
            <span aria-hidden="true">/</span>
            <MetaRow
              time={FOLDER_COPY.card.addedAt(
                formatUtcDate(file.uploadedAt, FOLDER_COPY.dateLocale),
              )}
              dateTime={file.uploadedAt}
              size="sm"
              className="gap-0"
            />
          </div>
        }
        trailing={
          hasRowActions(file, showRetention) ? (
            <RowActions
              file={file}
              confirming={confirming}
              busy={busy}
              showRetention={showRetention}
              onAsk={onAsk}
              onCancel={onCancel}
              onDelete={onDelete}
              onRetain={onRetain}
              onDownloadFailed={onDownloadFailed}
              onDownloadStarted={onDownloadStarted}
              onRehydrateFailed={onDownloadFailed}
              onRehydrateStarted={onDownloadStarted}
              onRehydrated={onRehydrated}
            />
          ) : undefined
        }
      />
    </article>
  );
}

/**
 * FIL-6: verification, as a dot rather than a digest — the same fact on both
 * sides of the space, because integrity verification is bilateral.
 *
 * **Colour is the column now: blue for verified, amber for pending, red for a
 * mismatch.** `StatusDot`'s own tones (`blue`/`amber`/`alert`) map directly —
 * this is the same three-state read `hashStatus` already gave `HashCell`, the
 * component this replaces, just carried by colour instead of by a truncated
 * value and a caption underneath it. The full value moved into the hover.
 *
 * **`hashStatus` decides, and the missing hash is only the backstop** — UPL-6
 * makes the status field authoritative, not the presence of a value: a hash
 * present before the ingest job has settled the row is not one anybody should
 * be invited to verify against. The second clause of the guard below keeps a
 * malformed row reading as pending instead of reaching into `sha256`.
 *
 * **Only the blue dot is a button.** Pending has nothing to copy yet, and a
 * mismatch is fully said by the dot itself — the column used to share that
 * job with a separate flag under the file name (`IntegrityFlag`), which made
 * a mismatch two things on screen saying one fact; the dot is the only place
 * it lives now. Red and amber render as plain, unfocusable `<span>`s; only a
 * verified hash is worth putting a hit target around.
 *
 * **Hover carries the information the old column showed as text** — `title`
 * is a native tooltip, not a custom popover: the content is one line, and a
 * portal-and-position rig built for `RowActions`' multi-item menu would be a
 * lot of machinery for a single string. Click, on the blue dot alone, copies
 * the full hash and swaps the dot for a green check for `bp-animate-check-confirm`'s
 * duration before handing the icon back — `setTimeout` owns the revert, not
 * the animation's own length, so the state holds just as long under
 * `prefers-reduced-motion`, which collapses the animation but not the timer.
 *
 * **Centered in the column, and equally boxed across all three states.**
 * Only the verified state is a `<button>`, and a button's own padding would
 * otherwise sit the blue dot visibly off from the plain amber and red
 * `<span>`s beside it in the rows above and below — `DOT_WRAPPER_CLASS` gives
 * every state the same padded box, and the outer `flex justify-center` centers
 * that box in the cell, so the padding a button needs for its own hit target
 * never reads as the dot itself drifting.
 */
const DOT_WRAPPER_CLASS = 'flex items-center justify-center rounded-md p-2';

function VerificationDot({ file }: { file: FileSummary }) {
  const [copied, setCopied] = useState(false);
  // Bumped on every click, and used as the check icon's `key` below — a
  // second click during the confirm window remounts it, so the animation
  // restarts from the same held state a first click gets, rather than
  // silently doing nothing because the element never changed identity.
  const [copyToken, setCopyToken] = useState(0);

  if (file.hashStatus === 'pending' || file.sha256 === undefined) {
    return (
      <span className="flex justify-center">
        <span title={FOLDER_COPY.verification.pending} className={DOT_WRAPPER_CLASS}>
          <StatusDot tone="amber" label={FOLDER_COPY.verification.pending} />
        </span>
      </span>
    );
  }

  if (file.hashStatus === 'mismatch') {
    return (
      <span className="flex justify-center">
        <span
          title={FOLDER_COPY.verification.mismatchHover(file.sha256)}
          className={DOT_WRAPPER_CLASS}
        >
          <StatusDot tone="alert" label={FOLDER_COPY.hash.mismatch} />
        </span>
      </span>
    );
  }

  const hash = file.sha256;

  async function copy() {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setCopyToken((current) => current + 1);
      // The dot takes its resting state back on its own; nothing else on this
      // row needs to know a copy happened, unlike a failed download or a
      // refused mutation.
      setTimeout(() => {
        setCopied(false);
      }, 1600);
    } catch {
      // The browser can refuse clipboard access. The dot stays as it was
      // rather than claiming a copy that did not happen — there is no
      // separate failed state here the way the space-link copy control
      // has one, because this control has nowhere to put the sentence that
      // explains it without growing back the caption this redesign removed.
    }
  }

  return (
    <span className="flex justify-center">
      <button
        type="button"
        aria-label={FOLDER_COPY.hash.copy(file.name)}
        title={FOLDER_COPY.verification.okHover(hash)}
        onClick={() => void copy()}
        className={`${DOT_WRAPPER_CLASS} transition-colors hover:bg-elevated`}
      >
        {copied ? (
          <span key={copyToken} className="bp-animate-check-confirm flex text-success">
            <CheckIcon />
          </span>
        ) : (
          <StatusDot tone="blue" />
        )}
      </button>
    </span>
  );
}

/** Lucide's `check`, hand-copied (no `lucide-react` dependency). */

/**
 * The file's title, and its only way to Download — replacing both the
 * dedicated Download column/button and the separate name cell, matching how
 * a folder's or a space's name is already the way in one level up
 * (`SpaceRoot`'s `FolderTable`, `AgencyGrid`'s `SpaceTable`).
 *
 * **A `<button>`, not a `<Link>`.** A folder has a route to navigate to; a
 * file does not, and its download URL is the minutes-lived SAS `DownloadButton`
 * used to warn against ever putting in an `href` (SEC-3) — so this stays an
 * `onClick`, styled to match, rather than becoming the thing that comment
 * warns about.
 *
 * **Inert, not hidden, while there is nothing to download.** `disabled`
 * covers the same three states `DownloadCell` used to branch on: mid-delete
 * confirm (download taken away while the row is mid-decision, so it does not
 * read as disagreeing with itself), and `Archive`/`Rehydrating` (no direct
 * download exists yet — `TierFlag`, rendered beside this, already says why,
 * and `RowActions`' overflow menu is where an archived row's retrieval
 * request now lives instead).
 */
function FileNameLink({
  file,
  disabled,
  onView,
  onDownloadFailed,
  onDownloadStarted,
}: {
  file: FileSummary;
  disabled: boolean;
  /**
   * FIL-8a. Called instead of downloading, and only when this file's own name
   * says a browser can paint it — the decision is made here rather than by the
   * caller because it is a property of the file, not of the table.
   */
  onView: (fileId: string) => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
}) {
  const { busy, download } = useFileDownload(file, onDownloadFailed, onDownloadStarted);

  // No size of its own, in either branch below: in a row it inherits the
  // cell's, on a card it inherits `CARD_TITLE_CLASS` from `CardBody`'s title
  // slot. It used to take an `isCardTitle` prop for a `text-lg` the tiles no
  // longer have.
  if (disabled) {
    return (
      <span className="cursor-default font-medium" style={{ color: token('bp-text') }}>
        {file.name}
      </span>
    );
  }

  /**
   * FIL-8a: for a photograph, the way in is looking at it.
   *
   * The name is already the row's anchor and already "the way in", matching a
   * folder's or a space's name one level up — and for an image that means the
   * viewer rather than the file system. Download does not go away; it moves
   * into this row's overflow (`RowActions`), which matters more than it looks:
   * without it a reader who only wants the file would have to open the viewer
   * to reach a download, and pay for two audited, metered transfers of it.
   */
  const viewable = isViewable(file.name);

  return (
    <button
      type="button"
      disabled={busy}
      aria-label={
        viewable ? FOLDER_COPY.view.action(file.name) : FOLDER_COPY.download.action(file.name)
      }
      onClick={() => {
        if (viewable) {
          onView(file.id);
          return;
        }
        void download();
      }}
      className="w-fit text-left font-medium hover:underline disabled:no-underline"
      style={{ color: token('bp-text') }}
    >
      {file.name}
    </button>
  );
}

/**
 * RET-4..RET-8, its own column now — a `Select` per row, immediate-apply,
 * seeded from `file.retentionClass`, on the same "trigger, not a standing
 * choice" reasoning the bulk dropdown's own doc gives, except this one *is* a
 * standing choice: unlike the folder-level convenience, a file's class is a
 * real, persistent fact, so this `Select` stays controlled at `file.retentionClass`
 * rather than resetting to a placeholder after firing.
 *
 * **Visible to anyone who can see the file, editable only where the API
 * agrees.** `disabled={!file.canSetRetention}` is visibility of the *act*, not
 * of the *fact* — PERM-6 lets a member see a class they may never set, the
 * same split `RetentionFlag`, the badge this replaces, already drew. The
 * caller gates the whole column on `showRetention` (RET-5's Evidence plan)
 * before any row is asked whether it may edit.
 *
 * **It reads the class and never the date for its own colour or copy.** A
 * released row keeps a stale `retentionUntil` on the server — a merge cannot
 * remove a property — and while the API's projection already withholds it, a
 * component that keyed on the date would be one refactor away from rendering
 * "Retained until 2033" on a file nobody is retaining (ADR-0021). The date is
 * still shown, once, as a caption under the dropdown — never as what decides
 * whether one renders.
 *
 * The direct choices below serve both card and table rows, with the same data,
 * permission gate, and immediate-save behavior.
 */
/** The shared file overflow's direct, immediate-apply retention choices. */

function RetentionMenu({
  file,
  busy,
  onRetain,
}: {
  file: FileSummary;
  busy: boolean;
  onRetain: (id: string, retentionClass: RetentionClass) => void;
}) {
  return (
    <div className="px-3 py-2">
      <Eyebrow>{FOLDER_COPY.columns.retention}</Eyebrow>
      <div role="group" aria-label={FOLDER_COPY.retention.picker(file.name)} className="mt-1">
        {retentionClassSchema.options.map((retentionClass) => {
          const selected = file.retentionClass === retentionClass;

          return (
            <button
              key={retentionClass}
              type="button"
              aria-pressed={selected}
              disabled={busy || !file.canSetRetention}
              onClick={() => {
                if (!selected) onRetain(file.id, retentionClass);
              }}
              className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body disabled:opacity-60"
            >
              <span className="flex size-3 shrink-0 items-center justify-center">
                {selected ? <CheckIcon /> : null}
              </span>
              {FOLDER_COPY.retention.menu[retentionClass]}
            </button>
          );
        })}
      </div>

      {file.retentionUntil === undefined ? null : (
        <span className="mt-1 block text-xs" style={{ color: token('bp-text-secondary') }}>
          {FOLDER_COPY.retention.until(formatUtcDate(file.retentionUntil, FOLDER_COPY.dateLocale))}
        </span>
      )}
    </div>
  );
}

/**
 * RET-9/RET-11: an unreadable file says so, under its name.
 *
 * `RetentionFlag`'s sibling rather than a branch inside it, because the two
 * answer different questions: one says what the agency bought, the other says
 * why Download is missing. A `Statutory` row carries both, and it should — the
 * class is the reason and the tier is the consequence.
 *
 * **`Hot`, `Cool`, and `Cold` render nothing, and that is the rule to keep.**
 * The badge exists to explain a missing button, not to expose Azure's tier model
 * to a reader who cannot act on it: §14 forbids a per-file tier control, and a
 * `COLD` chip on every Preserve row would be that control's ghost — a thing the
 * reader can see, cannot change, and has no reason to think about, on a file
 * that downloads instantly anyway (RET-6).
 */
function TierFlag({ file }: { file: FileSummary }) {
  if (file.tierState !== 'Archive' && file.tierState !== 'Rehydrating') return null;

  return <Badge tone="amber">{FOLDER_COPY.rehydration.badge[file.tierState]}</Badge>;
}

/**
 * The "expiring soon" hint (RET-3 as amended, RET-12).
 *
 * **A soft hint drawn from the server's `expiresAt`, gated on the room's window
 * (RET-12).** It renders only inside the room's warn lead — the same threshold
 * the sweep warns on (`warnLeadFor`), scaled so a 1-day room flags its files and
 * a default 180-day room only within 30 days of expiry. A file the server has
 * not yet stamped shows nothing, exactly like the server's own "not yet due".
 * `daysUntil` is clamped at zero, so a file already past its window reads
 * "today" rather than a negative count while it waits for the next sweep.
 */
function ExpiringFlag({
  file,
  windowDays,
}: {
  file: FileSummary;
  windowDays?: number | undefined;
}) {
  if (file.expiresAt === undefined || !isExpiringSoon(file.expiresAt, windowDays)) return null;

  return <Badge tone="amber">{FOLDER_COPY.expiring.soon(daysUntil(file.expiresAt))}</Badge>;
}

/**
 * Whether a row's overflow trigger has anything behind it.
 *
 * Four unrelated conditions now, and they are checked in three places — the
 * table cell, the card, and the menu's own body — so they live here rather
 * than being copied. `isViewable` is the newest of them (FIL-8a, FIL-8b): a
 * viewable file's name opens a reader, which means the overflow is the only
 * place its download can be, and a read-only member's photograph answers `false` to
 * every other condition in the list.
 */
function hasRowActions(file: FileSummary, showRetention: boolean): boolean {
  return file.canDelete || file.tierState === 'Archive' || showRetention || isViewable(file.name);
}

const OVERFLOW_GAP = 4;

const OVERFLOW_HEIGHT = { compact: 80, withRetention: 240 } as const;

/**
 * FIL-10's delete, and now RET-9's retrieval too, behind one overflow trigger.
 *
 * **Retention left this component entirely.** It used to be the row's other
 * act, sharing this same trigger and this same inline decision step; it has
 * its own column and its own `Select` now (immediate-apply, no confirm — RET-4
 * takes nothing away, so it never needed one), and this component narrowed
 * back to delete alone — until retrieval arrived, below.
 *
 * **Request retrieval moved in from its own standalone button** once the
 * dedicated Download column went away in favor of the file's title
 * (`FileNameLink`) — an archived row has no direct download for the title to
 * offer, so the act that used to stand in its place now lives here instead.
 *
 * **The trigger's own visibility had to widen to compensate.** It used to be
 * `file.canDelete` alone, because delete was the only thing behind it. RET-9
 * (PERM-6) requires retrieval be reachable by *every* principal on the space —
 * a read-only member with neither `canDelete` nor `canSetRetention` is often exactly
 * the reader who needs it — so the trigger now shows on `file.canDelete ||
 * file.tierState === 'Archive'` as well, and its menu renders only the items
 * that reader is actually offered (a read-only member on an archived row sees
 * Request retrieval and nothing else).
 *
 * **It confirms deletion because that takes something away** — the line
 * `MembersTab` draws between revoke and reactivate — but retrieval does not,
 * so it fires immediately, the same "trigger, not a choice" reasoning
 * the retention choice already uses. Inline rather than a modal, for the
 * same reason that component and `SpaceList` are: this workspace has no
 * confirm-dialog primitive, and one bought for a single caller is an
 * abstraction ahead of its need.
 *
 * A deleted file is recoverable for 30 days and then permanently gone (UPL-10)
 * — lighter than severing's confirm, which is irreversible (A-4). **The
 * confirm step carries no explanatory sentence**, at the product's request;
 * `Cancel` and `Confirm move` — the label naming where the file goes rather
 * than the verb the endpoint uses — are what is on screen while a row is
 * mid-decision.
 *
 * **Not `role="menu"`.** That promises arrow-key movement between items and a
 * roving tabindex this does not implement — the same reason
 * `SpaceRoot`'s action group is `role="group"` rather than `toolbar`. It is a
 * plain disclosure: a trigger and the panel it reveals.
 *
 * **Closes the way `SearchableSelect`'s listbox does** — `onBlur` with a short
 * deferred close, cancelled by the panel's own `onMouseDown` — rather than a
 * global document listener, so this stays consistent with the one other
 * popover in the workspace instead of inventing a second mechanism for the
 * same problem.
 *
 * **The panel renders through a portal, into `document.body`.** `DataTable`
 * wraps every table in `overflow-x-auto` so a wide one scrolls sideways
 * instead of blowing out the page — but per the CSS overflow spec, setting
 * `overflow-x` to anything but `visible` forces the used `overflow-y` to
 * `auto` too, whatever the class list says. An `absolute` panel positioned
 * inside that wrapper was being clipped at its bottom edge by that forced
 * vertical scrollport, which no `z-index` fixes because it is not a stacking
 * problem — the panel was never being drawn behind anything, it was being cut
 * off by an ancestor's box. `position: fixed` computed from the trigger's own
 * `getBoundingClientRect()` escapes that ancestor's overflow entirely, which
 * `absolute` cannot. The menu closes on scroll or resize rather than tracking
 * the trigger's position live — simpler, and a stale floating panel is worse
 * than one that closes a beat early.
 *
 * Both card and table views use this overflow's retention section, so the
 * table does not need a dedicated retention column.
 */
function RowActions({
  file,
  confirming,
  busy,
  showRetention = false,
  onAsk,
  onCancel,
  onDelete,
  onRetain,
  onDownloadFailed,
  onDownloadStarted,
  onRehydrateFailed,
  onRehydrateStarted,
  onRehydrated,
}: {
  file: FileSummary;
  confirming: boolean;
  busy: boolean;
  showRetention?: boolean;
  onAsk: (id: string) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onRetain?: (id: string, retentionClass: RetentionClass) => void;
  /** FIL-8a: an image's name opens the viewer, so its download lives here. */
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
  onRehydrateFailed: (message: string) => void;
  onRehydrateStarted: () => void;
  onRehydrated: (message: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    right: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const [rehydrating, setRehydrating] = useState(false);
  /**
   * FIL-8a. On a viewable image this is the row's *only* download: the name
   * above opens the viewer instead. Rendered nowhere else, so a reader who
   * wants the file pays for one audited, metered transfer rather than two.
   */
  const { busy: downloading, download } = useFileDownload(
    file,
    onDownloadFailed,
    onDownloadStarted,
  );
  const viewable = isViewable(file.name);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function rehydrate() {
    setMenuOpen(false);
    setRehydrating(true);
    onRehydrateStarted();

    try {
      const result = await requestRehydration(file.id);
      // `available` is a success the reader has to be told about, because the
      // screen was showing them an archived file a moment ago and the remedy
      // is now simply the title. A silent refetch would swap the row under
      // them with no explanation.
      onRehydrated(
        result.status === 'available'
          ? FOLDER_COPY.rehydration.available
          : FOLDER_COPY.rehydration.started,
      );
    } catch (caught) {
      onRehydrateFailed(messageFrom(caught, FOLDER_COPY.rehydration.failed));
    } finally {
      setRehydrating(false);
    }
  }

  useEffect(() => {
    if (!menuOpen) return;

    // Closes rather than re-tracks: a menu whose position goes stale mid-scroll
    // is a worse bug than one that closes a beat sooner than it strictly had to.
    function close() {
      setMenuOpen(false);
    }

    // `capture: true` so a scroll inside `DataTable`'s own scrollport — the
    // ancestor this portal exists to escape — closes the menu too, not only a
    // scroll of the page itself.
    window.addEventListener('scroll', close, { capture: true });
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  if (confirming) {
    return (
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className={BUTTON_CLASS}
          style={neutralButtonStyle()}
        >
          {FOLDER_COPY.delete.cancel}
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={() => {
            onDelete(file.id);
          }}
          className={BUTTON_CLASS}
          style={{ ...neutralButtonStyle(), color: token('bp-danger') }}
        >
          {busy ? FOLDER_COPY.delete.working : FOLDER_COPY.delete.confirm}
        </button>
      </span>
    );
  }

  return (
    <span className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={menuOpen}
        // Named per row for the same reason the menu item is: five buttons
        // reading "More actions" are five identical buttons to a screen
        // reader, and the row context that tells them apart is visual.
        aria-label={FOLDER_COPY.delete.more(file.name)}
        onClick={() => {
          setMenuOpen((open) => {
            const next = !open;
            if (next) {
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect) {
                const estimatedHeight =
                  showRetention && onRetain
                    ? OVERFLOW_HEIGHT.withRetention
                    : OVERFLOW_HEIGHT.compact;
                const right = window.innerWidth - rect.right;
                // A card can sit against the visible viewport's lower edge;
                // open into the available space above it instead of clipping
                // the actions below the screen.
                const opensAbove =
                  rect.bottom + OVERFLOW_GAP + estimatedHeight > window.innerHeight;

                setMenuPosition(
                  opensAbove
                    ? {
                        bottom: Math.max(
                          OVERFLOW_GAP,
                          window.innerHeight - rect.top + OVERFLOW_GAP,
                        ),
                        right,
                      }
                    : { top: rect.bottom + OVERFLOW_GAP, right },
                );
              } else {
                setMenuPosition(null);
              }
            }
            return next;
          });
        }}
        onBlur={() => {
          // Deferred so a click on the panel's own item lands before it
          // unmounts — the same timing `SearchableSelect` uses for its list.
          blurTimer.current = setTimeout(() => {
            setMenuOpen(false);
          }, 120);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setMenuOpen(false);
        }}
        /*
          Borderless and icon-only, unlike Download. An outlined second button
          per row gave the destructive act the same weight as the one every
          reader came for, and a table of them read as noise. The colour
          arrives on hover, where the intent is.

          `ml-auto`, not left to the `<td>`'s `text-align: right`. This button
          is `display: flex` — a block-level box — and `text-align` only
          shifts inline-level content; a block-level child inside the cell's
          wrapping `<span>` was landing at the left edge of the cell's content
          box regardless of the table's `align: 'end'`. `margin-left: auto`
          pushes the button itself to the right, which `text-align` cannot.
        */
        className="ml-auto flex items-center justify-center rounded-md p-1.5 text-secondary transition-colors hover:bg-elevated hover:text-body"
      >
        <MoreActionsIcon />
      </button>

      {menuOpen && menuPosition
        ? createPortal(
            <div
              className="fixed z-20 min-w-[11rem] overflow-hidden rounded-md border py-1 shadow-lg"
              style={{
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                right: menuPosition.right,
                borderColor: token('bp-border'),
                background: token('bp-panel'),
                boxShadow: token('shadow-md'),
              }}
              // Beats the trigger's blur timer so the click below commits.
              onMouseDown={() => {
                if (blurTimer.current) clearTimeout(blurTimer.current);
              }}
            >
              {showRetention && onRetain ? (
                <RetentionMenu file={file} busy={busy} onRetain={onRetain} />
              ) : null}

              {file.canDelete || file.tierState === 'Archive' || viewable ? (
                <div
                  className={showRetention && onRetain ? 'border-t py-1' : 'py-1'}
                  style={
                    showRetention && onRetain ? { borderColor: token('bp-border') } : undefined
                  }
                >
                  {viewable ? (
                    <button
                      type="button"
                      aria-label={FOLDER_COPY.download.action(file.name)}
                      disabled={downloading}
                      onClick={() => {
                        setMenuOpen(false);
                        void download();
                      }}
                      className="block w-full px-3 py-2 text-left text-xs font-medium text-secondary transition-colors hover:bg-elevated hover:text-body"
                    >
                      {downloading ? FOLDER_COPY.download.working : FOLDER_COPY.download.label}
                    </button>
                  ) : null}

                  {file.canDelete ? (
                    <button
                      type="button"
                      aria-label={FOLDER_COPY.delete.action(file.name)}
                      onClick={() => {
                        setMenuOpen(false);
                        onAsk(file.id);
                      }}
                      className="block w-full px-3 py-2 text-left text-xs font-medium text-secondary transition-colors hover:bg-elevated hover:text-danger"
                    >
                      {FOLDER_COPY.delete.label}
                    </button>
                  ) : null}

                  {file.tierState === 'Archive' ? (
                    <button
                      type="button"
                      aria-label={FOLDER_COPY.rehydration.action(file.name)}
                      disabled={rehydrating}
                      onClick={() => void rehydrate()}
                      className="block w-full px-3 py-2 text-left text-xs font-medium text-secondary transition-colors hover:bg-elevated hover:text-body"
                    >
                      {rehydrating
                        ? FOLDER_COPY.rehydration.working
                        : FOLDER_COPY.rehydration.label}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/** The overflow trigger's glyph — Lucide's `more-vertical`, hand-copied like `SpaceRoot`'s back chevron (no `lucide-react` dependency in this workspace). */
